import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaTimes, FaCog, FaUserPlus, FaPaperPlane, FaTrash, FaEdit, 
  FaUser, FaBuilding, FaSearch, FaFilter, FaSortAmountDown,
  FaChevronDown, FaChevronUp, FaUsersCog, FaSpinner,
  FaEnvelope, FaBars, FaThLarge, FaBriefcase, FaUserTie,
  FaEllipsisH, FaPlus, FaInfo, FaExclamationCircle,
  FaChevronLeft, FaChevronRight, FaAngleDoubleLeft, FaAngleDoubleRight
} from 'react-icons/fa';
import styles from '../CSS/AdminDepartamentoModal.module.css';
import EllipsisCell from '../../utils/EllipsisCell';
import { db } from '../../firebase';
import { collection, onSnapshot, query } from 'firebase/firestore';

const AdminDepartamentoModal = ({ 
  user, 
  departamentos, 
  adminsDepartamento: initialAdminsDepartamento, 
  onClose, 
  onCreate,
  onUpdate,
  onDelete
}) => {
  // Estados
  const [modo, setModo] = useState('listar');
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    departamento_id: departamentos.length > 0 ? departamentos[0].id : ''
  });
  const [selectedAdminId, setSelectedAdminId] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Estado local para adminsDepartamento con actualización en tiempo real
  const [adminsDepartamento, setAdminsDepartamento] = useState(initialAdminsDepartamento);
  
  // Estados para organización y filtrado
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartamento, setFilterDepartamento] = useState('');
  const [sortBy, setSortBy] = useState('nombre');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState('todos');
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  
  // Estado para el ancho de la ventana
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1000
  );
  
  // Suscripción en tiempo real a los cambios de adminsDepartamento
  useEffect(() => {
    const adminsQuery = query(collection(db, 'admins_departamento'));
    
    const unsubscribe = onSnapshot(adminsQuery, (snapshot) => {
      const updatedAdmins = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAdminsDepartamento(updatedAdmins);
    }, (error) => {
      console.error("Error al escuchar cambios en admins_departamento:", error);
    });
    
    // Cleanup
    return () => unsubscribe();
  }, []);
  
  // Efecto para actualizar el ancho de la ventana al redimensionar
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Verificar si el usuario puede eliminar
  const canDelete = true;
  
  // Resetear el formulario
  const resetForm = () => {
    setFormData({
      nombre: '',
      email: '',
      departamento_id: departamentos.length > 0 ? departamentos[0].id : ''
    });
    setErrors({});
    setSelectedAdminId(null);
  };
  
  // Agrupar administradores por departamento
  const adminsByDepartment = useMemo(() => {
    const result = {};
    departamentos.forEach(dep => {
      result[dep.id] = {
        id: dep.id,
        nombre: dep.nombre,
        admins: []
      };
    });
    
    adminsDepartamento.forEach(admin => {
      if (result[admin.departamento_id]) {
        result[admin.departamento_id].admins.push(admin);
      }
    });
    
    return result;
  }, [adminsDepartamento, departamentos]);
  
  // Obtener nombre del departamento
  const getDepartamentoNombre = (departamentoId) => {
    const departamento = departamentos.find(d => d.id === departamentoId);
    return departamento ? departamento.nombre : 'Desconocido';
  };
  
  // Filtrar y ordenar administradores
  const filteredAdmins = useMemo(() => {
    let filtered = [...adminsDepartamento];
    
    // Filtrar por búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(admin => 
        admin.nombre.toLowerCase().includes(term) || 
        admin.email.toLowerCase().includes(term)
      );
    }
    
    // Filtrar por departamento seleccionado
    if (selectedDepartment !== 'todos') {
      filtered = filtered.filter(admin => admin.departamento_id === selectedDepartment);
    }
    
    // Ordenar
    return filtered.sort((a, b) => {
      let valueA = a[sortBy];
      let valueB = b[sortBy];
      
      // Si estamos ordenando por departamento, usamos el nombre del departamento
      if (sortBy === 'departamento_id') {
        valueA = getDepartamentoNombre(a.departamento_id);
        valueB = getDepartamentoNombre(b.departamento_id);
      }
      
      // Comparar strings en minúsculas para un ordenamiento más natural
      if (typeof valueA === 'string') {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }
      
      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [adminsDepartamento, searchTerm, selectedDepartment, sortBy, sortDirection, departamentos]);
  
  // Calcular datos paginados
  const paginatedAdmins = useMemo(() => {
    const startIndex = (currentPage - 1) * 12;
    return filteredAdmins.slice(startIndex, startIndex + 12);
  }, [filteredAdmins, currentPage]);
  
  // Total de páginas
  const totalPages = useMemo(() => {
    return Math.ceil(filteredAdmins.length / 12);
  }, [filteredAdmins]);
  
  // Manejar cambio de página
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  // Estadísticas de departamentos
  const departmentStats = useMemo(() => {
    const stats = departamentos.map(dep => {
      const adminsCount = adminsDepartamento.filter(a => a.departamento_id === dep.id).length;
      return {
        id: dep.id,
        nombre: dep.nombre,
        count: adminsCount,
        percentage: adminsDepartamento.length > 0 
          ? Math.round((adminsCount / adminsDepartamento.length) * 100) 
          : 0
      };
    });
    
    return stats.sort((a, b) => b.count - a.count);
  }, [adminsDepartamento, departamentos]);
  
  // Cargar datos de un admin para editar
  const handleEditAdmin = (adminId) => {
    const admin = adminsDepartamento.find(a => a.id === adminId);
    if (admin) {
      setFormData({
        nombre: admin.nombre || '',
        email: admin.email || '',
        departamento_id: admin.departamento_id || ''
      });
      setSelectedAdminId(adminId);
      setModo('editar');
    }
  };
  
  // Manejar cambio de departamento seleccionado
  const handleDepartmentSelect = (depId) => {
    if (selectedDepartment === depId) {
      setSelectedDepartment('todos');
    } else {
      setSelectedDepartment(depId);
    }
    setCurrentPage(1);
  };
  
  // Manejar cambio de ordenamiento
  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };
  
  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Limpiar errores al cambiar el valor
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  // Validar el formulario
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es obligatorio';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'El email es obligatorio';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Formato de email inválido';
    }
    
    if (!formData.departamento_id) {
      newErrors.departamento_id = 'Debes seleccionar un departamento';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      if (modo === 'crear') {
        const success = await onCreate(formData);
        if (success) {
          resetForm();
          setModo('listar'); // Volver a la lista después de crear
        }
      } else if (modo === 'editar' && onUpdate) {
        const success = await onUpdate(selectedAdminId, formData);
        if (success) {
          resetForm();
          setModo('listar'); // Volver a la lista después de editar
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setErrors({ submit: 'Ha ocurrido un error. Inténtalo de nuevo.' });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Manejar eliminación de admin
  const handleDeleteAdmin = async (adminId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este administrador de departamento?')) {
      try {
        setIsSubmitting(true);
        const success = await onDelete(adminId);
        if (!success) {
          alert('Error al eliminar el administrador. Por favor, inténtalo de nuevo.');
        }
      } catch (error) {
        console.error('Error eliminando admin:', error);
        alert('Error al eliminar el administrador. Por favor, inténtalo de nuevo.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  // Generar un color para el departamento basado en su ID
  const getDepartmentColor = (departamentoId) => {
    const colors = [
      'var(--primary)',
      'var(--secondary)',
      'var(--warning)',
      'var(--purple)',
      'var(--teal)',
      'var(--indigo)'
    ];
    
    const numericId = typeof departamentoId === 'string' 
      ? departamentoId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
      : departamentoId;
    
    return colors[numericId % colors.length];
  };
  
  // Animaciones
  const modalVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };
  
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } }
  };
  
  const staggerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.2 } }
  };
  
  // Renderizar la barra de filtros y búsqueda
  const renderFilterBar = () => (
    <div className={styles.filterSection}>
      <div className={styles.searchBar}>
        <FaSearch className={styles.searchIcon} />
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Buscar administrador..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />
        {searchTerm && (
          <button
            className={styles.searchClear}
            onClick={() => {
              setSearchTerm('');
              setCurrentPage(1);
            }}
            title="Limpiar búsqueda"
            aria-label="Limpiar búsqueda"
          >
            <FaTimes />
          </button>
        )}
      </div>
      
      <div className={styles.filterControls}>
        <div className={styles.filterDropdown}>
          <button 
            className={`${styles.filterButton} ${selectedDepartment !== 'todos' ? styles.activeFilter : ''}`}
            onClick={() => setIsFilterVisible(!isFilterVisible)}
            aria-expanded={isFilterVisible}
            aria-haspopup="true"
          >
            <FaFilter />
            <span>
              {selectedDepartment !== 'todos' 
                ? `Departamento: ${getDepartamentoNombre(selectedDepartment)}` 
                : 'Filtros'}
            </span>
            <FaChevronDown className={isFilterVisible ? styles.rotated : ''} />
          </button>
          
          {isFilterVisible && (
            <div className={styles.filterMenu} role="menu">
              <div className={styles.filterMenuHeader}>
                <h4>Ordenar por</h4>
              </div>
              <div 
                className={styles.filterMenuItem} 
                onClick={() => handleSortChange('nombre')}
                role="menuitem"
                tabIndex={0}
              >
                <span>Nombre</span>
                {sortBy === 'nombre' && (
                  <FaChevronUp className={sortDirection === 'desc' ? styles.rotated : ''} />
                )}
              </div>
              <div 
                className={styles.filterMenuItem} 
                onClick={() => handleSortChange('email')}
                role="menuitem"
                tabIndex={0}
              >
                <span>Email</span>
                {sortBy === 'email' && (
                  <FaChevronUp className={sortDirection === 'desc' ? styles.rotated : ''} />
                )}
              </div>
              <div 
                className={styles.filterMenuItem} 
                onClick={() => handleSortChange('departamento_id')}
                role="menuitem"
                tabIndex={0}
              >
                <span>Departamento</span>
                {sortBy === 'departamento_id' && (
                  <FaChevronUp className={sortDirection === 'desc' ? styles.rotated : ''} />
                )}
              </div>
              
              <div className={styles.filterDivider} role="separator"></div>
              
              <div className={styles.filterMenuHeader}>
                <h4>Departamentos</h4>
              </div>
              <div 
                className={`${styles.filterMenuItem} ${selectedDepartment === 'todos' ? styles.active : ''}`}
                onClick={() => handleDepartmentSelect('todos')}
                role="menuitem"
                tabIndex={0}
                aria-selected={selectedDepartment === 'todos'}
              >
                <span>Todos</span>
              </div>
              {departmentStats.map(dep => (
                <div 
                  key={dep.id}
                  className={`${styles.filterMenuItem} ${selectedDepartment === dep.id ? styles.active : ''}`}
                  onClick={() => handleDepartmentSelect(dep.id)}
                  role="menuitem"
                  tabIndex={0}
                  aria-selected={selectedDepartment === dep.id}
                >
                  <span>{dep.nombre}</span>
                  <span className={styles.filterBadge}>{dep.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
  
  // Renderizar paginación
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    const pageButtons = [];
    
    // Primera página
    if (currentPage > 3) {
      pageButtons.push(
        <button
          key="first"
          className={styles.pageButton}
          onClick={() => handlePageChange(1)}
          aria-label="Primera página"
        >
          1
        </button>
      );
      
      if (currentPage > 4) {
        pageButtons.push(
          <span key="ellipsis1" className={styles.pageEllipsis}>...</span>
        );
      }
    }
    
    // Páginas cercanas a la actual
    for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
      pageButtons.push(
        <button
          key={i}
          className={`${styles.pageButton} ${currentPage === i ? styles.currentPage : ''}`}
          onClick={() => handlePageChange(i)}
          aria-label={`Página ${i}`}
          aria-current={currentPage === i ? 'page' : undefined}
        >
          {i}
        </button>
      );
    }
    
    // Última página
    if (currentPage < totalPages - 2) {
      if (currentPage < totalPages - 3) {
        pageButtons.push(
          <span key="ellipsis2" className={styles.pageEllipsis}>...</span>
        );
      }
      
      pageButtons.push(
        <button
          key="last"
          className={styles.pageButton}
          onClick={() => handlePageChange(totalPages)}
          aria-label="Última página"
        >
          {totalPages}
        </button>
      );
    }
    
    return (
      <div className={styles.pagination}>
        <button
          className={`${styles.pageButton} ${styles.navButton}`}
          onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          aria-label="Página anterior"
        >
          <FaChevronLeft />
        </button>
        
        {pageButtons}
        
        <button
          className={`${styles.pageButton} ${styles.navButton}`}
          onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          aria-label="Página siguiente"
        >
          <FaChevronRight />
        </button>
      </div>
    );
  };
  
  // Renderizar mensaje cuando un departamento no tiene administradores
  const renderEmptyDepartment = (departmentName) => (
    <div className={styles.emptyDepartment}>
      <FaExclamationCircle className={styles.emptyDepartmentIcon} />
      <h3 className={styles.emptyDepartmentTitle}>
        No hay administradores en {departmentName}
      </h3>
      <p className={styles.emptyDepartmentMessage}>
        Este departamento no tiene administradores asignados aún.
      </p>
      <button
        className={styles.emptyDepartmentButton}
        onClick={() => {
          resetForm();
          setFormData(prev => ({
            ...prev,
            departamento_id: selectedDepartment
          }));
          setModo('crear');
        }}
      >
        <FaUserPlus />
        <span>Añadir administrador</span>
      </button>
    </div>
  );
  
  // Renderizar vista de lista mejorada
  const renderAdminCards = () => {
    if (selectedDepartment !== 'todos' && filteredAdmins.length === 0 && adminsDepartamento.length > 0) {
      return renderEmptyDepartment(getDepartamentoNombre(selectedDepartment));
    }
    
    return (
      <div className={styles.adminCardsContainer}>
        <motion.div
          variants={staggerVariants}
          initial="hidden"
          animate="visible"
          className={styles.adminCards}
          role="grid"
        >
          <AnimatePresence>
            {paginatedAdmins.map(admin => {
              const departamentoNombre = getDepartamentoNombre(admin.departamento_id);
              const departmentColor = getDepartmentColor(admin.departamento_id);
              
              return (
                <motion.div
                  key={admin.id}
                  className={styles.adminCard}
                  variants={itemVariants}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                  role="row"
                >
                  <div className={styles.cardContent}>
                    <div 
                      className={styles.adminAvatar}
                      style={{ backgroundColor: `${departmentColor}20`, color: departmentColor }}
                    >
                      <FaUserTie />
                    </div>
                    <div className={styles.adminInfo}>
                      <h3 className={styles.adminName}>
                        <EllipsisCell content={admin.nombre} />
                      </h3>
                      <div className={styles.adminEmail}>
                        <FaEnvelope className={styles.emailIcon} />
                        <EllipsisCell content={admin.email} />
                      </div>
                      <div 
                        className={styles.departmentBadge}
                        style={{ 
                          backgroundColor: `${departmentColor}20`, 
                          color: departmentColor
                        }}
                      >
                        <FaBuilding className={styles.departmentIcon} />
                        <EllipsisCell content={departamentoNombre} />
                      </div>
                    </div>
                    <div className={styles.cardActions}>
                      <button
                        className={`${styles.actionButton} ${styles.editButton}`}
                        onClick={() => handleEditAdmin(admin.id)}
                        title="Editar"
                        aria-label={`Editar ${admin.nombre}`}
                        disabled={isSubmitting}
                      >
                        <FaEdit />
                      </button>
                      <button
                        className={`${styles.actionButton} ${styles.deleteButton}`}
                        onClick={() => handleDeleteAdmin(admin.id)}
                        title="Eliminar"
                        aria-label={`Eliminar ${admin.nombre}`}
                        disabled={isSubmitting}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      </div>
    );
  };
  
  // Renderizar resumen de departamentos
  const renderDepartmentSummary = () => {
    const departmentsWithAdmins = departmentStats.filter(d => d.count > 0);
    
    if (departmentsWithAdmins.length === 0) {
      return (
        <div className={styles.departmentSummary}>
          <h3 className={styles.summaryTitle}>
            <FaInfo className={styles.summaryIcon} />
            <span>Distribución por departamento</span>
          </h3>
          <div className={styles.noStats}>
            <FaExclamationCircle className={styles.noStatsIcon} />
            <p>No hay administradores asignados a departamentos</p>
          </div>
        </div>
      );
    }
    
    return (
      <div className={styles.departmentSummary}>
        <h3 className={styles.summaryTitle}>
          <FaInfo className={styles.summaryIcon} />
          <span>Distribución por departamento</span>
        </h3>
        <div className={styles.departmentStats}>
          {departmentStats.map(dep => (
            <div 
              key={dep.id} 
              className={`${styles.statItem} ${selectedDepartment === dep.id ? styles.activeStatItem : ''}`}
              onClick={() => handleDepartmentSelect(dep.id)}
              role="button"
              tabIndex={0}
              aria-label={`Filtrar por departamento ${dep.nombre}`}
              aria-pressed={selectedDepartment === dep.id}
            >
              <div className={styles.statHeader}>
                <span className={styles.statName}>{dep.nombre}</span>
                <span className={styles.statCount}>{dep.count}</span>
              </div>
              <div className={styles.statBar}>
                <div 
                  className={styles.statFill} 
                  style={{ 
                    width: `${dep.percentage}%`,
                    backgroundColor: getDepartmentColor(dep.id)
                  }}
                  aria-label={`${dep.percentage}%`}
                  role="progressbar"
                  aria-valuenow={dep.percentage}
                  aria-valuemin="0"
                  aria-valuemax="100"
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  // Renderizar vista vacía
  const renderEmptyState = () => (
    <div className={styles.emptyState} role="status">
      <div className={styles.emptyStateIcon}>
        <FaUsersCog />
      </div>
      <h3 className={styles.emptyStateTitle}>No hay administradores de departamento</h3>
      <p className={styles.emptyStateMessage}>
        Los administradores de departamento pueden gestionar los tickets asignados a sus departamentos.
      </p>
      <button
        className={styles.emptyStateButton}
        onClick={() => {
          resetForm();
          setModo('crear');
        }}
      >
        <FaUserPlus />
        <span>Crear primer administrador</span>
      </button>
    </div>
  );
  
  // Renderizar vista de lista de administradores
  const renderAdminsList = () => {
    if (adminsDepartamento.length === 0) {
      return renderEmptyState();
    }
    
    return (
      <div className={styles.adminListContainer}>
        <div className={styles.adminListHeader}>
          <div className={styles.headerTitle}>
            <h2 className={styles.headerText}>
              <FaUsersCog className={styles.headerIcon} />
              <span>Administradores de Departamento</span>
            </h2>
            <div className={styles.adminCount}>
              {adminsDepartamento.length} {adminsDepartamento.length === 1 ? 'administrador' : 'administradores'}
            </div>
          </div>
          
          <button
            className={styles.addButton}
            onClick={() => {
              resetForm();
              if (selectedDepartment !== 'todos') {
                setFormData(prev => ({
                  ...prev,
                  departamento_id: selectedDepartment
                }));
              }
              setModo('crear');
            }}
            aria-label="Añadir nuevo administrador"
          >
            <FaPlus />
            <span>Nuevo</span>
          </button>
        </div>
        
        {renderFilterBar()}
        
        {searchTerm && filteredAdmins.length === 0 ? (
          <div className={styles.noResults} role="status">
            <FaSearch className={styles.noResultsIcon} />
            <h3 className={styles.noResultsTitle}>No se encontraron resultados</h3>
            <p className={styles.noResultsMessage}>
              No hay coincidencias para "{searchTerm}"
            </p>
            <button
              className={styles.noResultsButton}
              onClick={() => setSearchTerm('')}
            >
              Limpiar búsqueda
            </button>
          </div>
        ) : (
          <>
            {selectedDepartment === 'todos' && !searchTerm && departmentStats.some(d => d.count > 0) && (
              renderDepartmentSummary()
            )}
            
            {renderAdminCards()}
            
            {filteredAdmins.length > 0 && renderPagination()}
          </>
        )}
      </div>
    );
  };
  
  // Renderizar formulario
  const renderForm = () => (
    <form onSubmit={handleSubmit} className={styles.adminForm} noValidate>
      {errors.submit && (
        <div className={styles.formError} role="alert">
          <FaExclamationCircle className={styles.errorIcon} />
          <span>{errors.submit}</span>
        </div>
      )}
      
      <div className={styles.formGroup}>
        <label htmlFor="nombre" className={styles.formLabel}>
          Nombre completo*
        </label>
        <div className={styles.inputWrapper}>
          <FaUserTie className={styles.inputIcon} />
          <input
            type="text"
            id="nombre"
            name="nombre"
            className={styles.formInput}
            value={formData.nombre}
            onChange={handleChange}
            placeholder="Nombre del administrador"
            disabled={isSubmitting}
            required
            aria-invalid={errors.nombre ? 'true' : 'false'}
            aria-describedby={errors.nombre ? 'nombre-error' : undefined}
          />
        </div>
        {errors.nombre && (
          <div className={styles.fieldError} id="nombre-error" role="alert">{errors.nombre}</div>
        )}
      </div>
      
      <div className={styles.formGroup}>
        <label htmlFor="email" className={styles.formLabel}>
          Email*
        </label>
        <div className={styles.inputWrapper}>
          <FaEnvelope className={styles.inputIcon} />
          <input
            type="email"
            id="email"
            name="email"
            className={styles.formInput}
            value={formData.email}
            onChange={handleChange}
            placeholder="ejemplo@correo.com"
            disabled={isSubmitting}
            required
            aria-invalid={errors.email ? 'true' : 'false'}
            aria-describedby={errors.email ? 'email-error' : undefined}
          />
        </div>
        {errors.email && (
          <div className={styles.fieldError} id="email-error" role="alert">{errors.email}</div>
        )}
      </div>
      
      <div className={styles.formGroup}>
        <label htmlFor="departamento_id" className={styles.formLabel}>
          Departamento*
        </label>
        <div className={styles.inputWrapper}>
          <FaBuilding className={styles.inputIcon} />
          <select
            id="departamento_id"
            name="departamento_id"
            className={styles.formInput}
            value={formData.departamento_id}
            onChange={handleChange}
            disabled={isSubmitting}
            required
            aria-invalid={errors.departamento_id ? 'true' : 'false'}
            aria-describedby="departamento-help departamento-error"
          >
            <option value="">-- Seleccionar departamento --</option>
            {departamentos.map(dep => (
              <option key={dep.id} value={dep.id}>{dep.nombre}</option>
            ))}
          </select>
        </div>
        {errors.departamento_id && (
          <div className={styles.fieldError} id="departamento-error" role="alert">{errors.departamento_id}</div>
        )}
        <small className={styles.helperText} id="departamento-help">
          El administrador podrá gestionar tickets de este departamento.
        </small>
      </div>
      
      <div className={styles.formActions}>
        <button
          type="button"
          className={styles.cancelButton}
          onClick={() => {
            resetForm();
            setModo('listar');
          }}
          disabled={isSubmitting}
        >
          <FaTimes />
          <span>Cancelar</span>
        </button>
        
        <button
          type="submit"
          className={styles.submitButton}
          disabled={isSubmitting}
          aria-busy={isSubmitting ? 'true' : 'false'}
        >
          {isSubmitting ? (
            <>
              <FaSpinner className={styles.spinnerIcon} aria-hidden="true" />
              <span>{modo === 'crear' ? 'Creando...' : 'Actualizando...'}</span>
            </>
          ) : (
            <>
              <FaPaperPlane aria-hidden="true" />
              <span>{modo === 'crear' ? 'Crear Admin' : 'Actualizar Admin'}</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
  
  // Renderizar contenido según el modo
  const renderContent = () => {
    switch (modo) {
      case 'crear':
        return (
          <>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                <FaUserPlus style={{ marginRight: '8px' }} />
                Crear Administrador de Departamento
              </h2>
              <button
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Cerrar modal"
              >
                <FaTimes />
              </button>
            </div>
            <div className={styles.modalContent}>
              {renderForm()}
            </div>
          </>
        );
      case 'editar':
        return (
          <>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                <FaEdit style={{ marginRight: '8px' }} />
                Editar Administrador de Departamento
              </h2>
              <button
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Cerrar modal"
              >
                <FaTimes />
              </button>
            </div>
            <div className={styles.modalContent}>
              {renderForm()}
            </div>
          </>
        );
      case 'listar':
      default:
        return (
          <>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle} id="modal-title">
                <FaCog className={styles.rotatingGear} style={{ marginRight: '8px' }} />
                Gestión de Administradores
              </h2>
              <button
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Cerrar modal"
              >
                <FaTimes />
              </button>
            </div>
            <div className={styles.modalContent}>
              {renderAdminsList()}
            </div>
          </>
        );
    }
  };
  
  return (
    <motion.div 
      className={styles.modalOverlay}
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={overlayVariants}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <motion.div 
        className={styles.modalContainer}
        variants={modalVariants}
      >
        {renderContent()}
      </motion.div>
    </motion.div>
  );
};

export default AdminDepartamentoModal;